/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/LicensingResults.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../compat/api';
import { useAuth } from '../compat/AuthContext';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import InfoTip from '../compat/InfoTip';
import type { Help } from '../compat/helpTypes';

const LicensingResultsHelp: Help = {
  title: 'Licensing Results',
  icon: '💷',
  purpose:
    'Every <strong>licensing analysis</strong> on record, one line per <strong>parent or standalone application</strong> — expand a parent to see its children. Each line carries the current disposition, the contract end date from Application Master Data, and a status showing where the contract stands.',
  doHere: [
    'Scan parents and standalones; use the <strong>▸</strong> to expand a parent and see its child applications.',
    'Read the family <strong>summary</strong> (rolled-up licence spend and savings) on each parent line.',
    'Check the <strong>Status</strong> icons for contract health (see the “i” above that column).',
    'Click a line to read that run’s <strong>narrative</strong> and the application’s run history.',
  ],
  fits: 'License Intelligence &rarr; Licensing Results',
  firstTime: 'Expand a parent (e.g. Microsoft 365) to see its child apps and the family’s rolled-up numbers.',
  sections: [
    {
      type: 'html',
      label: 'Does a run use the licensing document?',
      html:
        'Yes. For each application a run pulls any <strong>Knowledge Library</strong> document scoped to it (or its vendor-family parent), extracts the text, and gives it to the model as the licensing document — telling it to use the contract’s specifics rather than estimate. Runs without a document are estimates.',
    },
    {
      type: 'html',
      label: 'Why keep every run?',
      html:
        'For historical insight — chiefly to see the <strong>progression of a vendor’s asks</strong> over time. Runs are never overwritten: a new run becomes current, and previous runs stay as the price history (open a line to see them).',
    },
  ],
};

interface Row {
  run_id: string;
  application_id: string;
  application_id_code: string | null;
  application_name: string;
  vendor: string | null;
  vendor_family: string | null;
  disposition: string | null;
  annual_license_cost: number | null;
  savings_low: number | null;
  savings_mid: number | null;
  savings_high: number | null;
  new_license_mid: number | null;
  confidence: string | null;
  risk: string | null;
  has_document: boolean;
  contract_end_date: string | null;      // live, from Application Master Data
  run_contract_end_date: string | null;   // captured at run time
  rationale: string | null;
  methodology_note: string | null;
  year1_realization_note: string | null;
  started_at: string | null;
  status: string | null;
  parent_application_id: string | null;
  is_parent: boolean;
  parent_name: string | null;
  parent_code: string | null;
  parent_contract_end_date: string | null;
  parent_vendor: string | null;
  is_current: boolean;
  /** True for a group header we synthesise for a parent that has no analysis of its own. */
  synthetic?: boolean;
}

interface Group { header: Row; children: Row[]; isParent: boolean; }

const fmt$ = (n?: number | null) =>
  n == null || isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`;
const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

function dispColor(d?: string | null): string {
  const k = (d || '').toUpperCase();
  if (k.includes('RETIRE')) return '#c62828';
  if (k.includes('REPLACE')) return '#e64a19';
  if (k.includes('CONSOLIDAT')) return '#7b1fa2';
  if (k.includes('CLOUD')) return '#00838f';
  if (k.includes('MODERNIZE') || k.includes('RE-DESIGN')) return '#4527a0';
  if (k.includes('RENEGOT')) return '#ef6c00';
  if (k.includes('RIGHT') || k.includes('SIZE')) return '#0277bd';
  if (k.includes('RETAIN')) return '#2e7d32';
  return '#546e7a';
}
function DispPill({ d }: { d?: string | null }) {
  const c = dispColor(d);
  return (
    <span style={{ display: 'inline-block', fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap', color: c, background: `${c}18`, border: `1px solid ${c}55` }}>
      {d || '—'}
    </span>
  );
}

// Contract state from the (live) contract end date.
type CState = 'ok' | 'expiring6' | 'expiring2' | 'expired' | 'unknown';
function contractState(endDate?: string | null): CState {
  if (!endDate) return 'unknown';
  const d = new Date(endDate);
  if (isNaN(d.getTime())) return 'unknown';
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return 'expired';
  if (days <= 60) return 'expiring2';
  if (days <= 180) return 'expiring6';
  return 'ok';
}

const GREY = '#cbd5e1';
// App-wide standard: PowerPoint exports are always Office-PowerPoint red (same as Licensing Analysis).
const btnPpt: React.CSSProperties = { padding: '6px 12px', background: '#c43e1c', color: '#fff', border: '1px solid #c43e1c', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
// App-wide standard: Word exports are always Office-Word blue.
const btnWord: React.CSSProperties = { padding: '6px 12px', background: '#2b579a', color: '#fff', border: '1px solid #2b579a', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
// Tree controls are neutral chrome, not an export action — grey outline keeps them out of
// the way of the Word/PPT buttons.
const btnTree: React.CSSProperties = { height: 30, padding: '0 10px', background: '#fff', color: '#546e7a', border: '1px solid #cfd8dc', borderRadius: 7, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' };
// Family roll-up buttons sit inline on a parent row, so they are outlined rather than
// filled — the filled toolbar buttons stay the primary export action.
const btnFamWord: React.CSSProperties = { padding: '4px 9px', background: '#fff', color: '#2b579a', border: '1px solid #2b579a', borderRadius: 7, fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' };
const btnFamPpt: React.CSSProperties = { padding: '4px 9px', background: '#fff', color: '#c43e1c', border: '1px solid #c43e1c', borderRadius: 7, fontSize: 11, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' };
/** Three status icons: within-contract (green) · expiring (amber→orange) · expired (red).
 *  All greyed when the analysis is historic (a newer one exists) or the date is unknown. */
function StatusIcons({ endDate, isCurrent }: { endDate?: string | null; isCurrent: boolean }) {
  const st = isCurrent ? contractState(endDate) : 'unknown';
  const active = isCurrent && st !== 'unknown';
  const ic: React.CSSProperties = { fontSize: 19 };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} title={active ? undefined : (isCurrent ? 'No contract end date set' : 'Superseded by a newer analysis')}>
      <i className="material-icons" style={{ ...ic, color: st === 'ok' && active ? '#2e7d32' : GREY }} title="Within current contract">check_circle</i>
      <i className="material-icons" style={{ ...ic, color: st === 'expiring2' && active ? '#e65100' : (st === 'expiring6' && active ? '#f9a825' : GREY) }} title="Contract expiring soon">warning</i>
      <i className="material-icons" style={{ ...ic, color: st === 'expired' && active ? '#c62828' : GREY }} title="Contract expired">event_busy</i>
    </span>
  );
}

const StatusHelp = (
  <div style={{ fontSize: 13, lineHeight: 1.55 }}>
    <p style={{ marginTop: 0 }}>Where the application’s licensing contract stands, from its <strong>Contract end date</strong> (Application Master Data):</p>
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      <li><span style={{ color: '#2e7d32', fontWeight: 700 }}>● Green check</span> — analysis done and <strong>within</strong> the current contract (more than 6 months to run).</li>
      <li><span style={{ color: '#f9a825', fontWeight: 700 }}>● Amber warning</span> — contract expires within <strong>6 months</strong>.</li>
      <li><span style={{ color: '#e65100', fontWeight: 700 }}>● Orange warning</span> — higher urgency: expires within <strong>2 months</strong>.</li>
      <li><span style={{ color: '#c62828', fontWeight: 700 }}>● Red</span> — contract has <strong>expired</strong>.</li>
      <li><span style={{ color: GREY, fontWeight: 700 }}>● Grey (all)</span> — a newer analysis exists (this one is historic), or no contract end date is set.</li>
    </ul>
  </div>
);

/**
 * Checkbox with a third, "some but not all" state. `indeterminate` is a DOM property with no
 * HTML attribute, so React cannot set it declaratively — it has to be written via a ref.
 */
const TriCheckbox: React.FC<{
  checked: boolean; indeterminate: boolean; onChange: () => void; title?: string; style?: React.CSSProperties;
}> = ({ checked, indeterminate, onChange, title, style }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
  return (
    <input ref={ref} type="checkbox" checked={checked} onChange={onChange} title={title}
      onClick={(e) => e.stopPropagation()} style={style} />
  );
};

const LicensingResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const appIdParam = searchParams.get('applicationId');
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Administrator';
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<Row | null>(null);
  // Rows ticked for document generation. A run covers MANY applications, so a row is
  // identified by run + application — keying on run_id alone made every application of a
  // run tick together. Synthetic parent headers carry no analysis and are never selectable.
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const selKey = (r: Row) => `${r.run_id}::${r.application_id}`;
  const isSelected = (r: Row) => selectedRows.has(selKey(r));
  const toggleRow = (r: Row) => setSelectedRows((prev) => {
    const n = new Set(prev); const k = selKey(r);
    n.has(k) ? n.delete(k) : n.add(k); return n;
  });
  // Every analysed row of a family: the children, plus the parent's own analysis when it has
  // one (a synthetic parent is a header only — no analysis of its own to select).
  const selectableOf = (g: Group): Row[] => (g.header.synthetic ? g.children : [g.header, ...g.children]);
  const setGroupSelected = (g: Group, on: boolean) => setSelectedRows((prev) => {
    const n = new Set(prev);
    selectableOf(g).forEach((r) => (on ? n.add(selKey(r)) : n.delete(selKey(r))));
    return n;
  });

  // One rolled-up document for a whole family: the parent's own analysis (when it has one)
  // plus every analysed child, in a single Word/PPT. A synthetic parent header carries no
  // run of its own, so the run comes from a child.
  const [familyBusy, setFamilyBusy] = useState<string>('');
  const downloadFamily = async (g: Group, kind: 'ppt' | 'word') => {
    const runId = g.header.synthetic ? g.children[0]?.run_id : g.header.run_id;
    if (!runId) { setError('This family has no completed analysis to summarise.'); return; }
    setFamilyBusy(`${g.header.application_id}:${kind}`);
    try {
      if (kind === 'ppt') await apiService.downloadLicensingDeck(runId, undefined, undefined, g.header.application_id);
      else await apiService.downloadLicensingWord(runId, undefined, undefined, g.header.application_id);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Family summary generation failed.'); }
    finally { setFamilyBusy(''); }
  };

  // One document per selected APPLICATION — the server scopes the pack to that application
  // of the run, so ticking three applications yields three files.
  /**
   * One application's own pack.
   *
   * Replaces a tick-box selection plus a pair of buttons at the top of the screen that
   * looped and produced one file per ticked row. Two sets of Word/PPT buttons that looked
   * identical and did opposite things was the actual problem — the owner had to ask which
   * was which. Two capabilities now, one control each: the vendor band prints the vendor,
   * the row prints the row.
   */
  const [rowBusy, setRowBusy] = useState<string>('');
  const downloadRow = async (r: Row, kind: 'ppt' | 'word') => {
    if (String(r.run_id).startsWith('synthetic-')) return;
    setRowBusy(`${r.run_id}::${r.application_id}:${kind}`);
    try {
      if (kind === 'ppt') await apiService.downloadLicensingDeck(r.run_id, r.application_id);
      else await apiService.downloadLicensingWord(r.run_id, r.application_id);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Document generation failed.'); }
    finally { setRowBusy(''); }
  };

  /** Word + PPT for a single application, sized to sit inside a row. */
  const RowExport = ({ r }: { r: Row }) => (
    <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button onClick={() => downloadRow(r, 'word')} disabled={!!rowBusy}
        title={`Word document for ${r.application_id_code || r.application_name} only`}
        style={{ height: 26, padding: '0 9px', border: '1px solid #1d4ed8', background: 'rgba(29,78,216,0.10)', color: '#1d4ed8', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
        {rowBusy === `${r.run_id}::${r.application_id}:word` ? '…' : '⬇ Word'}
      </button>
      <button onClick={() => downloadRow(r, 'ppt')} disabled={!!rowBusy}
        title={`PowerPoint for ${r.application_id_code || r.application_name} only`}
        style={{ height: 26, padding: '0 9px', border: '1px solid #b91c1c', background: 'rgba(185,28,28,0.10)', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
        {rowBusy === `${r.run_id}::${r.application_id}:ppt` ? '…' : '⬇ PPT'}
      </button>
    </span>
  );

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await apiService.getLicensingResults(appIdParam || undefined)); }
    catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Failed to load licensing results.'); }
    finally { setLoading(false); }
  }, [appIdParam]);

  // Administrator-only: delete one application's analysis record (this version). Other versions
  // are kept; if it was the run's last application, the whole run is removed server-side.
  const deleteRecord = async (runId: string, applicationId: string, label: string) => {
    if (!window.confirm(`Delete this licensing analysis record for ${label}?\n\nOnly this analysis (this run's result for this application) is removed — other versions are kept. This cannot be undone.`)) return;
    try {
      await apiService.deleteLicensingAnalysisRecord(runId, applicationId);
      setSelectedRows((prev) => { const n = new Set(prev); n.delete(`${runId}::${applicationId}`); return n; });
      setOpenRow(null);
      await load();
    } catch (e: any) { alert(e?.response?.data?.error || e?.message || 'Delete failed'); }
  };
  useEffect(() => { load(); }, [load]);

  // Build the tree from the current (latest) analysis per application. Children are
  // grouped under their parent; a parent with no analysis of its own gets a synthetic
  // header row so it can still be shown and expanded.
  const { groups, historyByApp } = useMemo(() => {
    const current = rows.filter((r) => r.is_current);
    const byId = new Map(current.map((r) => [r.application_id, r] as const));
    const childrenByParent = new Map<string, Row[]>();
    for (const r of current) {
      if (r.parent_application_id) {
        const arr = childrenByParent.get(r.parent_application_id) || [];
        arr.push(r); childrenByParent.set(r.parent_application_id, arr);
      }
    }
    Array.from(childrenByParent.values()).forEach((arr) => arr.sort((a, b) => a.application_name.localeCompare(b.application_name)));

    const groups: Group[] = [];
    const handledParents = new Set<string>();
    // Standalones and real (analysed) parents — rows with no parent of their own.
    for (const r of current) {
      if (r.parent_application_id) continue;
      const kids = childrenByParent.get(r.application_id) || [];
      groups.push({ header: r, children: kids, isParent: kids.length > 0 });
      handledParents.add(r.application_id);
    }
    // Synthetic parents — a parent referenced by children but not itself analysed.
    Array.from(childrenByParent.entries()).forEach(([pid, kids]) => {
      if (handledParents.has(pid) || byId.has(pid)) return;
      const f = kids[0]!;
      const header: Row = {
        ...f,
        run_id: `synthetic-${pid}`,
        application_id: pid,
        application_id_code: f.parent_code,
        application_name: f.parent_name || '(parent application)',
        vendor: f.parent_vendor || f.vendor,
        contract_end_date: f.parent_contract_end_date,
        disposition: null, annual_license_cost: null, savings_low: null, savings_mid: null,
        savings_high: null, new_license_mid: null, rationale: null, methodology_note: null,
        year1_realization_note: null, parent_application_id: null, is_parent: true,
        is_current: true, synthetic: true,
      };
      groups.push({ header, children: kids, isParent: true });
    });
    groups.sort((a, b) => a.header.application_name.localeCompare(b.header.application_name));

    const historyByApp = new Map<string, Row[]>();
    for (const r of rows) {
      const arr = historyByApp.get(r.application_id) || [];
      arr.push(r); historyByApp.set(r.application_id, arr);
    }
    return { groups, historyByApp };
  }, [rows]);

  const matches = useCallback((r: Row) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (r.application_name || '').toLowerCase().includes(q)
      || (r.application_id_code || '').toLowerCase().includes(q)
      || (r.vendor || '').toLowerCase().includes(q);
  }, [search]);

  /**
   * Portfolio totals for the stat cards.
   *
   * Built from `rows.filter(is_current)` — NOT from the rendered groups. Results holds the
   * full history, so summing every row would count an application once per run it appears
   * in and inflate the savings; and the group list contains synthetic parent headers that
   * copy a child's figures, which would double-count again. One live analysis per
   * application is the only set that adds up.
   */
  const stats = useMemo(() => {
    const current = rows.filter((r) => r.is_current);
    const n = (v: any) => Number(v) || 0;
    const currentCost = current.reduce((s, r) => s + n(r.annual_license_cost), 0);
    const savingsMid = current.reduce((s, r) => s + n(r.savings_mid), 0);
    const savingsLow = current.reduce((s, r) => s + n(r.savings_low), 0);
    const savingsHigh = current.reduce((s, r) => s + n(r.savings_high), 0);
    // Proposed cost prefers the model's own figure and falls back to current - mid, so an
    // application whose run predates new_license_mid still contributes something sane.
    const proposed = current.reduce(
      (s, r) => s + (r.new_license_mid != null ? n(r.new_license_mid) : n(r.annual_license_cost) - n(r.savings_mid)), 0);
    const families = new Set(current.map((r) => r.vendor_family || r.vendor || '—')).size;
    const runs = new Set(current.map((r) => r.run_id)).size;
    const negotiable = current.filter((r) => (r.disposition || '').toUpperCase().includes('RENEGOTIATE')).length;
    return {
      apps: current.length, families, runs, negotiable,
      currentCost, savingsMid, savingsLow, savingsHigh, proposed,
      savingsPct: currentCost > 0 ? savingsMid / currentCost : 0,
    };
  }, [rows]);

  const visibleGroups = useMemo(
    () => groups.filter((g) => matches(g.header) || g.children.some(matches)),
    [groups, matches]
  );

  /**
   * VENDOR above PARENT.
   *
   * The parent/child grouping below answers "this application and its children". It
   * cannot express "everything we buy from Salesforce" — four independent applications
   * with no parent between them each became their own single-row group, which is what the
   * owner reported. A vendor negotiation is about the vendor, so the vendor is the outer
   * level and the parent tree nests inside it.
   */
  const vendorBuckets = useMemo(() => {
    const by = new Map<string, Group[]>();
    for (const g of visibleGroups) {
      const key = g.header.vendor_family || g.header.vendor || '—';
      const arr = by.get(key) || [];
      arr.push(g); by.set(key, arr);
    }
    return Array.from(by.entries())
      .map(([vendor, gs]) => {
        const allRows = gs.flatMap(selectableOf);
        return {
          vendor,
          groups: gs,
          rows: allRows,
          annual: allRows.reduce((t, r) => t + (Number(r.annual_license_cost) || 0), 0),
          savings: allRows.reduce((t, r) => t + (Number(r.savings_mid) || 0), 0),
        };
      })
      .sort((a, b) => b.annual - a.annual);
  }, [visibleGroups]);

  // One roll-up for a whole vendor. The server scopes on `vendor_family`, recorded on each
  // analysis row, so it needs no parent tree — see ScopeSelector.vendorFamily.
  const [vendorBusy, setVendorBusy] = useState<string>('');
  /**
   * Which vendor bands are open.
   *
   * Held as the EXPANDED set and starting empty, so the screen opens fully collapsed and
   * a vendor arriving from a new run is collapsed with the rest. The opposite — tracking
   * what is closed — was the first cut, and it made every new vendor spring open.
   *
   * Collapsed-by-default is the readable state once a real portfolio lands: the screen
   * answers "which vendors, and what are they worth" first, and you open the one you came
   * for. The vendor band already carries the count, the annual cost and the savings, so a
   * closed band is not a hidden one.
   */
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const toggleVendor = (v: string) => setExpandedVendors((prev) => {
    const nx = new Set(prev); nx.has(v) ? nx.delete(v) : nx.add(v); return nx;
  });
  const downloadVendor = async (vendor: string, rowsIn: Row[], kind: 'ppt' | 'word') => {
    const runId = rowsIn.find((r) => !String(r.run_id).startsWith('synthetic-'))?.run_id;
    if (!runId) { setError('This vendor has no completed analysis to summarise.'); return; }
    setVendorBusy(`${vendor}:${kind}`);
    try {
      if (kind === 'ppt') await apiService.downloadLicensingDeck(runId, undefined, undefined, undefined, vendor);
      else await apiService.downloadLicensingWord(runId, undefined, undefined, undefined, vendor);
    } catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Vendor summary generation failed.'); }
    finally { setVendorBusy(''); }
  };

  const toggle = (id: string) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // Expand / collapse every family currently in view. Only groups with children can open,
  // so standalone rows are ignored. `expanded` starts empty — collapsed is the default view,
  // and it resets to collapsed on every visit.
  const expandableIds = useMemo(
    () => visibleGroups.filter((g) => g.children.length > 0).map((g) => g.header.application_id),
    [visibleGroups],
  );
  const openCount = expandableIds.filter((id) => expanded.has(id)).length;
  const allOpen = expandableIds.length > 0 && openCount === expandableIds.length;
  // Expand/collapse acts on BOTH levels — vendors and the parent families inside them —
  // because a half-folded tree is not a state anyone asked for.
  const expandAll = () => { setExpanded(new Set(expandableIds)); setExpandedVendors(new Set(vendorBuckets.map((v) => v.vendor))); };
  const collapseAll = () => { setExpanded(new Set()); setExpandedVendors(new Set()); };

  // Depth of the child rows under their parent. Applied to the checkbox column on BOTH the
  // header and the body cells, so the whole child table — App ID and every column after it —
  // shifts together and the headers stay over their values.
  const CHILD_INDENT = 72;
  // Quieter type across the tree. Bold was carrying no information — every row had it,
  // so it distinguished nothing and only made a dense screen louder. Weight is now
  // reserved for the two things that are genuinely emphasis: a savings figure and a
  // vendor name.
  /* The height of the scrolling list, MEASURED rather than inherited.
     ------------------------------------------------------------------
     Locking the header wants exactly one bounded scroll box. Three attempts at getting
     one from CSS alone all failed against the shell: `height: 100%` resolved against an
     auto-height <main> and grew; `position: sticky` bound itself to `.apr-app`, which
     carries `overflow-y: auto` and so counts as the scrollport whether or not it is the
     thing actually scrolling. Rather than reach up and change a wrapper the whole product
     shares, this screen measures where it starts and takes the rest of the window.
     Re-measured on resize, because a fixed calc() would be wrong at every other size. */
  const pageRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = pageRef.current;
      if (!el) return;
      // 44px keeps the footer line ("Powered by …") clear of the list's last row.
      setBoxHeight(Math.max(320, window.innerHeight - el.getBoundingClientRect().top - 44));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8a95a8', fontWeight: 600, padding: '8px 12px', background: '#f4f6fa', borderBottom: '1px solid #e9edf3', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '9px 12px', borderBottom: '1px solid #eef1f6', fontSize: 12.5, fontWeight: 400, verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  /** The group row's cells — same metrics as the child cells, so the two read as one table. */
  const gtd: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5, fontWeight: 400, verticalAlign: 'middle', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', borderBottom: '1px solid #e9edf3' };

  // Family rollup for the summary shown on a parent line: the analysed children, plus the
  // parent's own analysis when it has one (synthetic parents have none).
  const familyTotals = (g: Group) => {
    const all = g.header.synthetic ? g.children : [g.header, ...g.children];
    const annual = all.reduce((s, r) => s + (Number(r.annual_license_cost) || 0), 0);
    const savings = all.reduce((s, r) => s + (Number(r.savings_mid) || 0), 0);
    return { annual, savings, count: g.children.length };
  };

  // A child application row inside an expanded parent card (same columns as the header).
  const childRow = (r: Row) => {
  // The row whose details are open reads as selected — a left rail and a tinted
  // background, the same treatment the disposition and cost screens use. Without it the
  // panel could be describing any of the rows behind it.
  const open = !!openRow && openRow.run_id === r.run_id && openRow.application_id === r.application_id;
  return (
    <tr key={`${r.run_id}-${r.application_id}`}
      onClick={() => setOpenRow(r)}
      style={{ cursor: 'pointer', background: open ? '#e8f0fe' : 'transparent', boxShadow: open ? 'inset 3px 0 0 #1565c0' : 'none' }}
      onMouseEnter={(e) => { if (!open) (e.currentTarget as HTMLElement).style.background = '#f8f9fa'; }}
      onMouseLeave={(e) => { if (!open) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}>
      {/* Indented so the tick sits under the parent's own checkbox and next to the child it
          belongs to, rather than flush against the card edge a long way from the row. */}
      <td style={{ ...td, paddingLeft: 30 }} onClick={(e) => e.stopPropagation()}>
        {null}
      </td>
      <td style={{ ...td, fontFamily: 'ui-monospace, Menlo, monospace', color: '#1565c0' }}>{r.application_id_code || '—'}</td>
      <td style={{ ...td, color: '#263238' }} title={r.application_name}>{r.application_name}</td>
      <td style={{ ...td, color: '#55627a' }} title={r.vendor || ''}>{r.vendor || '—'}</td>
      <td style={{ ...td, fontFamily: 'ui-monospace, Menlo, monospace' }}>{fmtDate(r.started_at)}</td>
      <td style={{ ...td, fontFamily: 'ui-monospace, Menlo, monospace' }}>{r.contract_end_date ? fmtDate(r.contract_end_date) : '—'}</td>
      <td style={{ ...td, textAlign: 'center' }}><StatusIcons endDate={r.contract_end_date} isCurrent={r.is_current} /></td>
      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(r.annual_license_cost)}</td>
      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#2e7d32', fontWeight: 600 }}>{fmt$(r.savings_mid)}</td>
      <td style={td}><DispPill d={r.disposition} /></td>
      <td style={{ ...td }} onClick={(e) => e.stopPropagation()}><RowExport r={r} /></td>
      <td style={{ ...td, color: '#1565c0' }}>View →</td>
    </tr>
  );
  };

  /**
   * The one place the group-row columns are defined.
   *
   *  tick · caret · application ·  run  · contract · status · annual · savings · disposition · export · view
   *
   * ONE export track. A parent fills it with its family pair, a standalone with its own —
   * never both, and never a track that appears only sometimes, which is what knocked the
   * columns out of line to begin with.
   */
  /**
   * ONE column definition for the whole tree — the group row AND the child table.
   *
   * They used to disagree: the group row merged App ID, Application and Vendor into a
   * single cell while the table split them into three, so a parent's figures could never
   * line up with its children's. And the group row had no horizontal scroll, so below
   * about 1200px its flexible column collapsed and "Microsoft 365" wrapped one word per
   * line while the table beside it stayed intact.
   *
   * Fixed widths, one array, rendered as a <colgroup> in both tables. Both sit in the
   * same horizontal scroll container at the same min-width, so the columns stay locked to
   * each other at any viewport and the card scrolls sideways rather than mangling text.
   */
  const COL_W = [54, 88, 164, 150, 80, 92, 86, 100, 116, 148, 136, 62] as const;
  const TABLE_MIN = COL_W.reduce((a, b) => a + b, 0);
  const ColGroup = () => (
    <colgroup>{COL_W.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
  );

  const HeaderStat = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{children}</div>
    </div>
  );

  return (
    /* Title, stat band and filter are LOCKED; the vendor list scrolls under them. With a
       real portfolio you are past the first vendor immediately, and the totals and the
       filter used to leave the screen at that point.

       STICKY, not a flex height lock. The first attempt made this page a column of
       `height: 100%` with the list as the only scroller — and it silently did nothing,
       because the shell's <main> is `min-height: auto` with `overflow: visible` and grows
       to its content, so `height: 100%` resolved against an auto-height parent and the
       DOCUMENT went on scrolling. Sticky resolves against whatever actually scrolls, so it
       holds without this screen having to make assumptions about the shell.

       `overflow: visible` on `.page` is load-bearing, and `top: 0` follows from it. APR's
       stylesheet gives `.page` `overflow-y: auto`, so it became a scroll container — and a
       sticky element sticks to its NEAREST scroll container, not to the one the user is
       actually scrolling. The band pinned itself to `.page`'s box, which never scrolls,
       while the real scroller moved underneath: rows rode straight up over the header.

       Taking that overflow off hands the scrolling to `.apr-app`, which the shell already
       gives a bounded height. Its scrollport starts BELOW the fixed AppBar, so the offset
       is 0 — an earlier `top: 64` (the AppBar's height) would push the band down by a bar
       it is no longer measured against.

       `height: auto` is the third half of it. APR's CSS pins `.page` to `height: 100%`,
       and a sticky element can only travel inside its own containing block — so the band
       stuck for one viewport and then scrolled away with the bottom of a box that was
       shorter than the list inside it. Letting `.page` grow to its content gives the band
       the whole list to stay pinned across. */
    <div ref={pageRef} className="page"
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden',
               height: boxHeight ?? undefined, minHeight: 0 }}>
      <HelpPanel help={LicensingResultsHelp} />
      <div style={{ flexShrink: 0, background: '#fff', zIndex: 3, boxShadow: '0 6px 10px -8px rgba(15,23,42,0.35)' }}>
      <PageHeader
        title="Licensing Results"
        subtitle="Turn every savings case into a negotiation-ready pack — and track how each vendor's asks move between runs."
      />

      {/* Same money line as Licensing Analysis, so the two screens reconcile — plus the
          coverage figures that only make sense here, where the question is what the
          portfolio adds up to rather than what a single run is about to do. */}
      {stats.apps > 0 && (
        <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, margin: '0 0 10px' }}>
          <ResultStat label="Applications analysed" value={String(stats.apps)}
            sub={`${stats.families} vendor famil${stats.families === 1 ? 'y' : 'ies'} · ${stats.runs} run${stats.runs === 1 ? '' : 's'}`} />
          <ResultStat label="Current annual cost" value={fmt$(stats.currentCost)} />
          <ResultStat label="Est. savings (mid)" value={fmt$(stats.savingsMid)}
            sub={`${Math.round(stats.savingsPct * 100)}% · low ${fmt$(stats.savingsLow)} · high ${fmt$(stats.savingsHigh)}`}
            color="#2e7d32" />
          <ResultStat label="Proposed new cost" value={fmt$(stats.proposed)} />
          <ResultStat label="Flagged to renegotiate" value={String(stats.negotiable)}
            sub={`of ${stats.apps} analysed`} />
        </div>
      )}

      <div style={{ flexShrink: 0, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by application, ID, or vendor…"
          style={{ height: 40, padding: '0 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', minWidth: 280, flex: '0 1 380px' }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#546e7a', fontWeight: 600 }}>
          Contract status <InfoTip title="Contract status">{StatusHelp}</InfoTip>
        </span>
        {appIdParam && <a href="/apr/license-intelligence/licensing-results" style={{ fontSize: 12.5, color: '#1565c0', fontWeight: 600 }}>Show all applications</a>}
        {/* Shown whenever there is anything to fold — vendors count now, not only parent
            families. Gated on families alone, these buttons were absent on a screen of
            standalone applications, which is exactly the screen with nothing to collapse
            by hand. */}
        {(vendorBuckets.length > 0 || expandableIds.length > 0) && (() => {
          const everythingOpen = expandedVendors.size === vendorBuckets.length && (expandableIds.length === 0 || allOpen);
          const everythingShut = expandedVendors.size === 0 && openCount === 0;
          return (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button onClick={expandAll} disabled={everythingOpen}
                style={{ ...btnTree, opacity: everythingOpen ? 0.45 : 1, cursor: everythingOpen ? 'default' : 'pointer' }}
                title={`Expand all ${vendorBuckets.length} vendor${vendorBuckets.length === 1 ? '' : 's'}${expandableIds.length ? ` and ${expandableIds.length} famil${expandableIds.length === 1 ? 'y' : 'ies'}` : ''}`}>⊞ Expand all</button>
              <button onClick={collapseAll} disabled={everythingShut}
                style={{ ...btnTree, opacity: everythingShut ? 0.45 : 1, cursor: everythingShut ? 'default' : 'pointer' }}
                title="Collapse every vendor and family">⊟ Collapse all</button>
            </div>
          );
        })()}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* The tick-box selection and its Word/PPT pair lived here. They produced one
              file per ticked row, which is what the vendor band's Combined pair and the
              per-row pair now cover between them — one control per capability. */}
          <span style={{ fontSize: 12.5, color: '#8a95a8' }}>
            {loading ? 'Loading…' : `${visibleGroups.length} application${visibleGroups.length === 1 ? '' : 's'}`}
          </span>
        </div>
      </div>
      </div>

      {error && <div style={{ fontSize: 13, color: '#c62828', marginBottom: 12 }}>{error}</div>}

      {!loading && visibleGroups.length === 0 && !error ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8a95a8' }}>
          No licensing analyses on record yet. Run one from <strong>Licensing Analysis</strong>.
        </div>
      ) : (
        <div className="tree-view-container" style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 12, paddingRight: 4 }}>
          {/* THE column header — one for the screen, above the first vendor band, and
              pinned so it never scrolls away. It used to live inside each parent's child
              table, which put it BELOW the row it was naming and repeated it down the
              page.

              It sits INSIDE the scrolling list rather than in the locked band above,
              because that is what makes it align: as a child of the same box as the
              vendor cards it inherits the same width, with no scrollbar to guess at.

              The two transparent-bordered wrappers MIRROR the boxes a row actually sits
              in — the vendor bucket (1px border + 12px padding) and the application card
              (1px border). Reproducing the chain rather than adding up a margin is what
              makes this exact: at this display's pixel ratio a "1px" border measures
              0.62px, so 13px of margin landed the header three quarters of a pixel off
              the rows and one and a half pixels narrower. The browser applies the same
              rounding to both when both are laid out the same way. */}
          <div style={{ position: 'sticky', top: 0, zIndex: 2, background: '#f4f6fa', borderBottom: '1px solid #dbe1ea' }}>
            <div style={{ border: '1px solid transparent', padding: '0 12px' }}>
              <div style={{ border: '1px solid transparent', overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: TABLE_MIN, tableLayout: 'fixed' }}>
                  <ColGroup />
                  <thead>
                    <tr>
                      <th style={{ ...th, paddingLeft: 30, background: 'transparent' }}></th>
                      <th style={{ ...th, background: 'transparent' }}>App ID</th><th style={{ ...th, background: 'transparent' }}>Application</th><th style={{ ...th, background: 'transparent' }}>Vendor</th>
                      <th style={{ ...th, background: 'transparent' }}>Run date</th><th style={{ ...th, background: 'transparent' }}>Contract end</th><th style={{ ...th, background: 'transparent', textAlign: 'center' }}>Status</th>
                      <th style={{ ...th, background: 'transparent', textAlign: 'right' }}>Annual $</th><th style={{ ...th, background: 'transparent', textAlign: 'right' }}>Savings (mid)</th>
                      <th style={{ ...th, background: 'transparent' }}>Disposition</th><th style={{ ...th, background: 'transparent' }}>Download</th><th style={{ ...th, background: 'transparent' }}></th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>
          </div>

          {vendorBuckets.map((vb) => (
          <div key={vb.vendor}>
            {/* VENDOR band. Word/PPT here summarise every analysed application of this
                vendor; the buttons on the cards below still scope to a parent or a single
                application, so both levels are available. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', border: '1px solid #c7d2fe', borderBottom: 'none', background: '#eef2ff' }}>
              {/* TriCheckbox reports a click, not a target state, so the next state is
                  derived here — same as the parent header below. */}
              <TriCheckbox
                checked={vb.rows.length > 0 && vb.rows.every(isSelected)}
                indeterminate={vb.rows.some(isSelected) && !vb.rows.every(isSelected)}
                onChange={() => {
                  const turnOn = !(vb.rows.length > 0 && vb.rows.every(isSelected));
                  setSelectedRows((prev) => {
                    const nx = new Set(prev);
                    vb.rows.forEach((r) => (turnOn ? nx.add(selKey(r)) : nx.delete(selKey(r))));
                    return nx;
                  });
                }}
                title={`Select every analysed application from ${vb.vendor}`}
              />
              <span
                onClick={() => toggleVendor(vb.vendor)}
                title={expandedVendors.has(vb.vendor) ? `Collapse ${vb.vendor}` : `Expand ${vb.vendor}`}
                style={{ fontSize: 13, color: '#4f46e5', width: 14, flexShrink: 0, cursor: 'pointer', transform: expandedVendors.has(vb.vendor) ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s' }}>▶</span>
              <span onClick={() => toggleVendor(vb.vendor)} style={{ fontSize: 13.5, fontWeight: 600, color: '#3730a3', minWidth: 0, cursor: 'pointer' }}>{vb.vendor}</span>
              <span style={{ fontSize: 12, color: '#4f46e5' }}>
                {vb.rows.length} application{vb.rows.length === 1 ? '' : 's'} · {fmt$(vb.annual)} annual · <strong style={{ color: '#166534' }}>{fmt$(vb.savings)}</strong> savings
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
                {/* Labelled "combined" because the buttons at the top of the screen look
                    identical and do the opposite — one file per ticked application. The
                    difference is many files versus one, and nothing but the wording says
                    so. */}
                <button onClick={() => downloadVendor(vb.vendor, vb.rows, 'word')} disabled={!!vendorBusy}
                  title={`ONE combined Word document covering all ${vb.rows.length} analysed ${vb.vendor} application${vb.rows.length === 1 ? '' : 's'}`}
                  style={{ height: 28, padding: '0 10px', border: '1px solid #1d4ed8', background: 'rgba(29,78,216,0.10)', color: '#1d4ed8', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                  {vendorBusy === `${vb.vendor}:word` ? '…' : '⭳ Combined Word'}
                </button>
                <button onClick={() => downloadVendor(vb.vendor, vb.rows, 'ppt')} disabled={!!vendorBusy}
                  title={`ONE combined PPT deck covering all ${vb.rows.length} analysed ${vb.vendor} application${vb.rows.length === 1 ? '' : 's'}`}
                  style={{ height: 28, padding: '0 10px', border: '1px solid #b91c1c', background: 'rgba(185,28,28,0.10)', color: '#b91c1c', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                  {vendorBusy === `${vb.vendor}:ppt` ? '…' : '⭳ Combined PPT'}
                </button>
              </span>
            </div>
            {expandedVendors.has(vb.vendor) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, border: '1px solid #c7d2fe', borderTop: 'none', padding: 12, background: '#fbfcff' }}>
          {vb.groups.map((g) => {
            const h = g.header;
            const hasKids = g.children.length > 0;
            const isExpanded = expanded.has(h.application_id);
            const selectable = selectableOf(g);
            const selectedCount = selectable.filter(isSelected).length;
            const groupAll = selectable.length > 0 && selectedCount === selectable.length;
            const groupSome = selectedCount > 0;
            const summary = g.isParent ? familyTotals(g) : null;
            const annual = summary ? summary.annual : h.annual_license_cost;
            const savings = summary ? summary.savings : h.savings_mid;
            return (
              <div key={h.application_id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', background: '#fff' }}>
                {/* Parent / standalone header node — a TABLE ROW sharing the child
                    table's colgroup, so a parent's figures sit directly above its
                    children's in the same columns. */}
                <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: TABLE_MIN, tableLayout: 'fixed' }}>
                  <ColGroup />
                  <tbody>
                    <tr
                      onClick={() => hasKids && toggle(h.application_id)}
                      style={{
                        backgroundColor: openRow && openRow.run_id === h.run_id && openRow.application_id === h.application_id ? '#e8f0fe' : '#f8f9fa',
                        boxShadow: openRow && openRow.run_id === h.run_id && openRow.application_id === h.application_id ? 'inset 3px 0 0 #1565c0' : 'none',
                        cursor: hasKids ? 'pointer' : 'default',
                      }}>
                      <td style={{ ...gtd, paddingLeft: 14, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {hasKids
                          ? <TriCheckbox
                              checked={groupAll}
                              indeterminate={groupSome && !groupAll}
                              onChange={() => setGroupSelected(g, !groupAll)}
                              style={{ width: 14, height: 14, cursor: 'pointer', verticalAlign: 'middle' }}
                              title={groupAll ? 'Clear this family' : `Select all ${selectable.length} analyses in this family`} />
                          : null}
                        <span onClick={() => hasKids && toggle(h.application_id)}
                          style={{ marginLeft: 6, fontSize: 11, color: '#8a95a8', display: 'inline-block', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .15s', visibility: hasKids ? 'visible' : 'hidden' }}>▶</span>
                      </td>
                      <td style={{ ...gtd, fontFamily: 'ui-monospace, Menlo, monospace', color: '#1565c0' }}>{h.application_id_code || '—'}</td>
                      <td style={{ ...gtd, color: '#263238' }} title={h.application_name}>{h.application_name}</td>
                      <td style={{ ...gtd, color: '#78909c' }} title={h.vendor || ''}>{h.vendor || '—'}</td>
                      <td style={{ ...gtd, fontFamily: 'ui-monospace, Menlo, monospace' }}>{h.synthetic ? '—' : fmtDate(h.started_at)}</td>
                      <td style={{ ...gtd, fontFamily: 'ui-monospace, Menlo, monospace' }}>{h.contract_end_date ? fmtDate(h.contract_end_date) : '—'}</td>
                      <td style={{ ...gtd, textAlign: 'center' }}><StatusIcons endDate={h.contract_end_date} isCurrent={h.is_current} /></td>
                      <td style={{ ...gtd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt$(annual)}</td>
                      <td style={{ ...gtd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#2e7d32', fontWeight: 600 }}>{fmt$(savings)}</td>
                      <td style={gtd}>{h.synthetic ? <span style={{ fontSize: 11, color: '#8a95a8' }}>family roll-up</span> : <DispPill d={h.disposition} />}</td>
                      <td style={gtd} onClick={(e) => e.stopPropagation()}>
                        {/* ONE export pair per row. A parent's covers the family — its
                            figures on this line already are the family roll-up — and its
                            children each carry their own when expanded. */}
                        {hasKids ? (
                          <span style={{ display: 'inline-flex', gap: 6 }}>
                            <button onClick={() => downloadFamily(g, 'word')} disabled={!!familyBusy}
                              style={{ ...btnFamWord, opacity: familyBusy ? 0.5 : 1 }}
                              title={`One Word document covering ${h.application_id_code || h.application_name} and its ${g.children.length} application${g.children.length === 1 ? '' : 's'}`}>
                              {familyBusy === `${h.application_id}:word` ? '…' : '⬇ Word'}
                            </button>
                            <button onClick={() => downloadFamily(g, 'ppt')} disabled={!!familyBusy}
                              style={{ ...btnFamPpt, opacity: familyBusy ? 0.5 : 1 }}
                              title={`One PowerPoint covering ${h.application_id_code || h.application_name} and its ${g.children.length} application${g.children.length === 1 ? '' : 's'}`}>
                              {familyBusy === `${h.application_id}:ppt` ? '…' : '⬇ PPT'}
                            </button>
                          </span>
                        ) : h.synthetic ? null : <RowExport r={h} />}
                      </td>
                      <td style={{ ...gtd, color: '#1565c0' }}>
                        {!h.synthetic && <span onClick={(e) => { e.stopPropagation(); setOpenRow(h); }} style={{ cursor: 'pointer' }}>View →</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
                </div>

                {/* Children table. No <thead> of its own — the column names are stated
                    once at the top of the vendor, above the first application, rather
                    than reappearing under every parent row. */}
                {hasKids && isExpanded && (
                  <div style={{ overflowX: 'auto' }}>
                    {/* Same colgroup and min-width as the group row above, so a child's
                        figures land in the same columns as its parent's and the two
                        scroll together. */}
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: TABLE_MIN, tableLayout: 'fixed' }}>
                      <ColGroup />
                      <tbody>{g.children.map(childRow)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
            </div>
            )}
          </div>
          ))}
        </div>
      )}

      {openRow && (
        <NarrativeDrawer
          row={openRow}
          history={historyByApp.get(openRow.application_id) || []}
          isSelected={isSelected}
          onToggle={toggleRow}
          onOpenHistory={(h) => setOpenRow(h)}
          onClose={() => setOpenRow(null)}
          isAdmin={isAdmin}
          onDelete={deleteRecord}
        />
      )}
    </div>
  );
};

function NarrativeDrawer({ row, history, isSelected, onToggle, onOpenHistory, onClose, isAdmin, onDelete }: {
  row: Row; history: Row[]; isSelected: (r: Row) => boolean; onToggle: (r: Row) => void; onOpenHistory: (r: Row) => void; onClose: () => void;
  isAdmin?: boolean; onDelete?: (runId: string, applicationId: string, label: string) => void;
}) {
  const canDelete = (r: Row) => !!isAdmin && !!onDelete && !r.synthetic && !String(r.run_id).startsWith('synthetic-');
  return (
    <>
      {/* ABOVE the shell's chrome.
          At z-index 1001 this drawer sat UNDER both the platform app bar (1201) and the
          anchored footer bar (1199): the bar covered the header — which is where Close
          lives, so there was no way out of the panel — and the footer clipped the Delete
          band at the bottom. Both were reported, and both were this one number.
          1600/1601 clears the app bar, the footer and the nav drawer. */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1600 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(680px, 96vw)', background: '#fff', zIndex: 1601, boxShadow: '-8px 0 30px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eceff1', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, fontSize: 13, color: '#1565c0' }}>{row.application_id_code}</span>
              <span style={{ fontWeight: 700, fontSize: 17, color: '#212121' }}>{row.application_name}</span>
              {row.is_current
                ? <span style={{ fontSize: 9.5, fontWeight: 800, color: '#fff', background: '#1565c0', borderRadius: 20, padding: '2px 7px' }}>CURRENT</span>
                : <span style={{ fontSize: 9.5, fontWeight: 800, color: '#78909c', background: '#eceff1', borderRadius: 20, padding: '2px 7px' }}>HISTORIC</span>}
            </div>
            <div style={{ fontSize: 12.5, color: '#78909c', marginTop: 3 }}>
              {row.vendor || '—'} · run {fmtDate(row.started_at)}
              {row.contract_end_date ? ` · contract end ${fmtDate(row.contract_end_date)}` : ''}
              {row.has_document ? ' · document used' : ' · estimate (no document)'}
            </div>
          </div>
          {/* Delete has moved to the pinned footer. It sat here, at the top right of a
              panel whose content scrolls for pages, and was read as part of the title
              row rather than as the action it is. */}
          <button onClick={onClose} aria-label="Close" title="Close"
            style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="material-icons" style={{ fontSize: 20 }}>close</i>
          </button>
        </div>
        {/* LOCKED. The disposition and the four figures — annual cost, proposed, savings
            and the range — are the numbers the whole panel exists to state. They were
            inside the scroll region, so they left the screen as soon as you started
            reading the rationale that justifies them, and you were then reading an
            argument with the conclusion off-screen. */}
        <div style={{ flexShrink: 0, padding: '14px 20px', borderBottom: '1px solid #eceff1', background: '#fbfcfe' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <DispPill d={row.disposition} />
            {row.confidence && <span style={{ fontSize: 12, color: '#607d8b' }}>{row.confidence} confidence</span>}
            {row.risk && <span style={{ fontSize: 12, color: '#607d8b' }}>· {row.risk} risk</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <MiniStat label="Annual license" value={fmt$(row.annual_license_cost)} />
            <MiniStat label="Proposed (mid)" value={fmt$(row.new_license_mid)} />
            <MiniStat label="Savings (mid)" value={fmt$(row.savings_mid)} color="#2e7d32" />
            <MiniStat label="Savings range" value={`${fmt$(row.savings_low)} – ${fmt$(row.savings_high)}`} />
          </div>
        </div>

        {/* The ONLY scrolling region: the analysis itself. */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {row.rationale && <Section title="Rationale" body={row.rationale} />}
          {row.methodology_note && <Section title="Methodology" body={row.methodology_note} />}
          {row.year1_realization_note && <Section title="Year-1 realization" body={row.year1_realization_note} />}
          {!row.rationale && !row.methodology_note && !row.year1_realization_note && (
            <div style={{ color: '#9e9e9e', fontSize: 13 }}>No narrative recorded for this run.</div>
          )}

          {history.length > 1 && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #eceff1' }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5e35b1', marginBottom: 8 }}>Run history · {history.length}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {history.map((h) => (
                  <div key={h.run_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={isSelected(h)} onChange={() => onToggle(h)} style={{ flexShrink: 0, cursor: 'pointer' }} title="Select this analysis for PPT / Word" />
                    <button onClick={() => onOpenHistory(h)}
                      style={{ flex: 1, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, border: `1px solid ${h.run_id === row.run_id ? '#bbdefb' : '#eceff1'}`, background: h.run_id === row.run_id ? '#f3f8ff' : '#fff', cursor: 'pointer', fontSize: 12.5 }}>
                      <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', color: '#546e7a', minWidth: 78 }}>{fmtDate(h.started_at)}</span>
                      <DispPill d={h.disposition} />
                      <span style={{ color: '#2e7d32', fontWeight: 700, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{fmt$(h.savings_mid)}</span>
                      {h.is_current && <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', background: '#1565c0', borderRadius: 20, padding: '1px 6px' }}>CURRENT</span>}
                    </button>
                    {canDelete(h) && (
                      <button onClick={() => onDelete!(h.run_id, h.application_id, `${h.application_id_code || ''} ${h.application_name || ''} (${fmtDate(h.started_at)})`.trim())}
                        title="Delete this analysis version (Administrator only)"
                        style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, border: '1.5px solid #fca5a5', background: '#fef2f2', color: '#b91c1c', cursor: 'pointer', fontSize: 13 }}>🗑</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* PINNED. Delete is the one destructive action here and it was at the end of a
            panel that scrolls for pages — on a long analysis you could not reach it at
            all, which is exactly how it was reported. Its own band at the foot, always
            on screen, always the same place. */}
        {canDelete(row) && (
          <div style={{ flexShrink: 0, padding: '12px 20px', borderTop: '1px solid #eceff1', background: '#fff', display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => onDelete!(row.run_id, row.application_id, `${row.application_id_code || ''} ${row.application_name || ''}`.trim())}
              style={{ padding: '0 14px', height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}
              title="Delete this analysis record (Administrator only)">🗑 Delete this analysis</button>
            <span style={{ fontSize: 11.5, color: '#78909c', minWidth: 0 }}>
              Removes this run's result for this application. Other versions are kept.
            </span>
          </div>
        )}
      </div>
    </>
  );
}

function MiniStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || '#37474f', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Section({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#5e35b1', marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: '#37474f', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{body}</div>
    </div>
  );
}

/** Portfolio stat card for the Results header. Mirrors the Kpi card on Licensing Analysis
 *  so the two screens read as one module rather than two different products. */
const ResultStat: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  // Roughly half the height it was: 14px padding -> 8/10, 22px figure -> 16, and the
  // sub-line dropped to 10.5. The band summarises the list; it should not out-weigh it,
  // and with a real portfolio arriving the rows are what the screen is for.
  <div style={{ background: '#fff', border: '1px solid #e9edf3', borderRadius: 8, padding: '8px 10px' }}>
    <div style={{ fontSize: 9.5, color: '#6b7280', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || '#111827', marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
  </div>
);

export default LicensingResults;
